import { useEffect, useState, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { RefreshCw, Download } from "lucide-react"

interface Commission {
  id: string
  created_at: string
  member_id: string
  type: string
  amount: number
  status: string
  product_slug: string | null
  notes: string | null
  users?: { email: string; full_name: string | null } | null
}

export function CommissionsPage() {
  const { toast } = useToast()
  const [data, setData]     = useState<Commission[]>([])
  const [loading, setLoading] = useState(true)
  const [month, setMonth]   = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from("commission_transactions")
        .select("*, users(email, full_name)")
        .order("created_at", { ascending: false })
        .limit(200)

      if (month) {
        const start = new Date(`${month}-01T00:00:00Z`)
        const end   = new Date(start.getFullYear(), start.getMonth() + 1, 1)
        query = query.gte("created_at", start.toISOString()).lt("created_at", end.toISOString())
      }

      const { data: rows, error } = await query
      if (error) throw error
      setData((rows ?? []) as Commission[])
    } catch { toast("Failed to load commissions", "error") }
    finally { setLoading(false) }
  }, [month, toast])

  useEffect(() => { load() }, [load])

  const total = data.reduce((sum, c) => sum + (c.amount ?? 0), 0)
  const emojiMap: Record<string, string> = { zimrah: "🌸", hpc: "📅", almanac: "🌾" }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-zimrah-500 transition-all" />
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => toast("Export coming in Phase 5", "info")}>Export</Button>
        <span className="text-sm font-black text-gray-900 ml-auto">
          Total: <span className="text-green-600">${total.toFixed(2)}</span>
        </span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Date", "Recipient", "Type", "Product", "Amount", "Status"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={6} /> :
               data.length === 0 ? (
                <tr><td colSpan={6}>
                  <EmptyState icon="📈" title="No commissions" subtitle="Commissions for this period will appear here." />
                </td></tr>
               ) : data.map(c => (
                <tr key={c.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-semibold text-sm text-gray-900">{c.users?.full_name ?? "—"}</p>
                    <p className="text-xs text-gray-400">{c.users?.email ?? c.member_id.slice(0, 8)}</p>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="purple">{c.type?.replace(/_/g, " ")}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    {c.product_slug
                      ? <span className="text-lg" title={c.product_slug}>{emojiMap[c.product_slug] ?? "📱"}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-6 py-4 font-black text-gray-900 tabular-nums">
                    ${(c.amount ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4">
                    {c.status === "paid"    ? <Badge variant="success">Paid</Badge> :
                     c.status === "pending" ? <Badge variant="warning">Pending</Badge> :
                     c.status === "voided"  ? <Badge variant="danger">Voided</Badge> :
                     <Badge variant="muted">{c.status}</Badge>}
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}